import { useEffect, useState } from 'react';
import {
  Link2,
  Link2Off,
  Sparkles,
  Save,
  Wand2,
  Package,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface XeroStatus {
  connected: boolean;
  tenant_name?: string;
  tenant_id?: string;
  connected_by?: string;
  connected_at?: string;
}

interface XeroDefaults {
  labour_account_code?: string | null;
  parts_account_code?: string | null;
  labour_rate?: number | null;
  reference_prefix?: string | null;
  labour_description_template?: string | null;
  line_amount_type?: string | null;
  currency_code?: string | null;
  tax_type_labour?: string | null;
  tax_type_parts?: string | null;
}

interface AccountItem { code: string; name: string; type: string }

export default function Settings() {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState<XeroStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [defaults, setDefaults] = useState<XeroDefaults>({});
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [detectingTypes, setDetectingTypes] = useState(false);
  const [learningParts, setLearningParts] = useState(false);
  const [intel, setIntel] = useState<{ jobTypes: number; jobTypesDetected: boolean; jobTypesAt?: string; parts: number }>({
    jobTypes: 0,
    jobTypesDetected: false,
    parts: 0,
  });

  const loadIntel = async () => {
    try {
      const [jt, cs] = await Promise.all([
        api.get('/job-types'),
        api.get('/consumables'),
      ]);
      setIntel({
        jobTypes: jt.data.job_types?.length || 0,
        jobTypesDetected: !!jt.data.detected,
        jobTypesAt: jt.data.detected_at,
        parts: (cs.data || []).filter((c: any) => c.source === 'xero_learned').length,
      });
    } catch (e) { /* ignore */ }
  };

  const loadStatus = async () => {
    try {
      const res = await api.get('/xero/status');
      setStatus(res.data);
    } catch (e) {
      setStatus({ connected: false });
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadDefaults = async () => {
    try {
      const res = await api.get('/xero/defaults');
      setDefaults(res.data || {});
    } catch (e) { /* ignore */ }
  };

  const loadAccounts = async () => {
    try {
      const res = await api.get('/xero/accounts', { params: { revenue_only: true } });
      setAccounts(res.data.accounts || []);
    } catch (e) { /* ignore — requires Xero connection */ }
  };

  useEffect(() => {
    loadStatus();
    loadDefaults();
    loadIntel();
  }, []);

  useEffect(() => {
    if (status?.connected) loadAccounts();
  }, [status?.connected]);

  const connect = async () => {
    try {
      const res = await api.get('/xero/connect');
      const url = res.data.auth_url;
      const popup = window.open(url, 'xero-connect', 'width=560,height=720');
      // Poll every 2s to detect connection
      const interval = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(interval);
          await loadStatus();
          await loadDefaults();
          const r = await api.get('/xero/status').catch(() => ({ data: null }));
          if (r.data?.connected) toast.success(`Connected to ${r.data.tenant_name}`);
        }
      }, 1500);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to start Xero connect');
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect Xero? Push-to-Xero will stop working until reconnected.')) return;
    try {
      await api.post('/xero/disconnect', null, { params: { confirm: true } });
      toast.success('Disconnected from Xero');
      await loadStatus();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const autoDetect = async () => {
    setAutoDetecting(true);
    try {
      const res = await api.post('/xero/defaults/auto-detect', null, { params: { count: 20 } });
      toast.success(`Detected defaults from ${res.data.sample_count} invoices`);
      await loadDefaults();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Auto-detect failed');
    } finally {
      setAutoDetecting(false);
    }
  };

  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      const payload: any = {};
      Object.entries(defaults).forEach(([k, v]) => {
        if (v !== null && v !== '' && v !== undefined) payload[k] = v;
      });
      await api.put('/xero/defaults', payload);
      toast.success('Defaults saved');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingDefaults(false);
    }
  };

  const detectJobTypes = async () => {
    setDetectingTypes(true);
    try {
      const res = await api.post('/xero/detect-job-types', null, { params: { count: 50 } });
      toast.success(`Detected ${res.data.count} job types from ${res.data.sample_size} descriptions`);
      await loadIntel();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'AI job-type detection failed');
    } finally {
      setDetectingTypes(false);
    }
  };

  const learnParts = async () => {
    setLearningParts(true);
    try {
      const res = await api.post('/xero/learn-parts', null, { params: { count: 50, merge_into_inventory: true } });
      toast.success(`Learned ${(res.data as any).count || (res.data as any).learned_count || 'new'} parts from Xero`);
      await loadIntel();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'AI parts learning failed');
    } finally {
      setLearningParts(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-slate-600">Settings are only available to admin users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Xero connection, invoicing defaults and AI helpers.</p>
      </div>

      {/* Connection */}
      <div className="card p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center">
            <Link2 className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="font-bold text-slate-900">Xero Connection</h2>
            <p className="text-xs text-slate-500">OAuth 2.0 — tokens are refreshed automatically.</p>
          </div>
        </div>

        {loadingStatus ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking…
          </div>
        ) : status?.connected ? (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="text-sm font-bold text-emerald-800">{status.tenant_name}</div>
                <div className="text-xs text-emerald-600">
                  Connected by {status.connected_by} · {status.connected_at ? new Date(status.connected_at).toLocaleDateString() : ''}
                </div>
              </div>
            </div>
            <button onClick={disconnect} className="btn btn-secondary">
              <Link2Off className="h-4 w-4" /> Disconnect
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Not connected</span>
            </div>
            <button onClick={connect} className="btn btn-primary">
              <Link2 className="h-4 w-4" /> Connect Xero
            </button>
          </div>
        )}
      </div>

      {/* Defaults */}
      <div className="card p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 grid place-items-center">
              <Sparkles className="h-5 w-5 text-brand-700" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">Invoicing Defaults</h2>
              <p className="text-xs text-slate-500">Used when pushing jobs to Xero.</p>
            </div>
          </div>
          <button
            onClick={autoDetect}
            disabled={!status?.connected || autoDetecting}
            className="btn btn-secondary"
            title={!status?.connected ? 'Connect Xero first' : ''}
          >
            {autoDetecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Auto-detect from Xero
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Labour rate (£ / hour)">
            <input
              type="number"
              step={0.5}
              className="input"
              value={defaults.labour_rate ?? ''}
              onChange={(e) => setDefaults({ ...defaults, labour_rate: e.target.value === '' ? null : Number(e.target.value) })}
              placeholder="e.g. 65"
            />
          </Field>
          <Field label="Reference prefix">
            <input
              type="text"
              className="input"
              value={defaults.reference_prefix ?? ''}
              onChange={(e) => setDefaults({ ...defaults, reference_prefix: e.target.value })}
              placeholder="e.g. JOB-"
            />
          </Field>
          <Field label="Labour account code">
            {accounts.length > 0 ? (
              <select
                className="input"
                value={defaults.labour_account_code ?? ''}
                onChange={(e) => setDefaults({ ...defaults, labour_account_code: e.target.value })}
              >
                <option value="">Select account…</option>
                {accounts.map((a) => (
                  <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="input"
                value={defaults.labour_account_code ?? ''}
                onChange={(e) => setDefaults({ ...defaults, labour_account_code: e.target.value })}
                placeholder="e.g. 200"
              />
            )}
          </Field>
          <Field label="Parts account code">
            {accounts.length > 0 ? (
              <select
                className="input"
                value={defaults.parts_account_code ?? ''}
                onChange={(e) => setDefaults({ ...defaults, parts_account_code: e.target.value })}
              >
                <option value="">Select account…</option>
                {accounts.map((a) => (
                  <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="input"
                value={defaults.parts_account_code ?? ''}
                onChange={(e) => setDefaults({ ...defaults, parts_account_code: e.target.value })}
                placeholder="e.g. 310"
              />
            )}
          </Field>
          <Field label="Currency code">
            <input
              type="text"
              className="input"
              value={defaults.currency_code ?? ''}
              onChange={(e) => setDefaults({ ...defaults, currency_code: e.target.value.toUpperCase() })}
              placeholder="GBP"
              maxLength={3}
            />
          </Field>
          <Field label="Line amount type">
            <select
              className="input"
              value={defaults.line_amount_type ?? 'Exclusive'}
              onChange={(e) => setDefaults({ ...defaults, line_amount_type: e.target.value })}
            >
              <option value="Exclusive">Exclusive of tax</option>
              <option value="Inclusive">Inclusive of tax</option>
              <option value="NoTax">No tax</option>
            </select>
          </Field>
          <Field label="Labour tax type">
            <input
              type="text"
              className="input"
              value={defaults.tax_type_labour ?? ''}
              onChange={(e) => setDefaults({ ...defaults, tax_type_labour: e.target.value })}
              placeholder="e.g. OUTPUT2 (UK VAT 20%)"
            />
          </Field>
          <Field label="Parts tax type">
            <input
              type="text"
              className="input"
              value={defaults.tax_type_parts ?? ''}
              onChange={(e) => setDefaults({ ...defaults, tax_type_parts: e.target.value })}
              placeholder="e.g. OUTPUT2"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Labour description template">
              <input
                type="text"
                className="input"
                value={defaults.labour_description_template ?? ''}
                onChange={(e) => setDefaults({ ...defaults, labour_description_template: e.target.value })}
                placeholder="Labour - {title} ({job_number})"
              />
              <p className="text-xs text-slate-400 mt-1">Placeholders: <code className="font-mono bg-slate-100 px-1 rounded">{'{title}'}</code>, <code className="font-mono bg-slate-100 px-1 rounded">{'{job_number}'}</code></p>
            </Field>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button onClick={saveDefaults} disabled={savingDefaults} className="btn btn-primary">
            {savingDefaults ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save defaults
          </button>
        </div>
      </div>

      {/* AI Learn */}
      <div className="card p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-purple-50 grid place-items-center">
            <Sparkles className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="font-bold text-slate-900">AI-Powered Learning</h2>
            <p className="text-xs text-slate-500">Analyse your Xero history to auto-detect job categories and parts.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-purple-600" />
                <h3 className="font-semibold text-slate-900">Detect job types</h3>
              </div>
              {intel.jobTypesDetected && (
                <span className="chip bg-purple-100 text-purple-800 ring-1 ring-inset ring-purple-200 text-[10px]">
                  {intel.jobTypes} learned
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Read your last 50 Xero invoices and cluster into 6–12 job categories.
            </p>
            <button
              onClick={detectJobTypes}
              disabled={!status?.connected || detectingTypes}
              className="btn btn-secondary w-full"
            >
              {detectingTypes ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              {detectingTypes ? 'Detecting…' : intel.jobTypesDetected ? 'Re-run detection' : 'Run detection'}
            </button>
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-purple-600" />
                <h3 className="font-semibold text-slate-900">Learn parts</h3>
              </div>
              {intel.parts > 0 && (
                <span className="chip bg-purple-100 text-purple-800 ring-1 ring-inset ring-purple-200 text-[10px]">
                  {intel.parts} parts
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Extract parts + typical prices from your last 50 invoices.
            </p>
            <button
              onClick={learnParts}
              disabled={!status?.connected || learningParts}
              className="btn btn-secondary w-full"
            >
              {learningParts ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
              {learningParts ? 'Learning…' : intel.parts > 0 ? 'Re-learn' : 'Run learning'}
            </button>
          </div>
        </div>

        {!status?.connected && (
          <p className="text-xs text-slate-400 mt-4 text-center">
            Connect Xero to enable AI learning.
          </p>
        )}
      </div>

      <div className="text-center text-xs text-slate-400">
        <a
          href="https://developer.xero.com/"
          target="_blank"
          rel="noreferrer"
          className="hover:text-slate-600 inline-flex items-center gap-1"
        >
          Xero developer docs <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}
